/**
 * Replaces an url keywords string with the matching key in the object and add the unmatched keys at the end as query params
 * @example
 * urlParamsReplace('/api/:id', { id: '123' }) // => '/api/123'
 * urlParamsReplace('/api/:id', { id: '123', name: 'test' }) // => '/api/123?name=test'
 * @param url The url to replace
 * @param object The object to replace the url with
 * @returns The new url
 */
export const urlParamsReplace = (url: string, object: { [key: string]: string }) => {
  if (object == null || Object.keys(object).length <= 0) {
    return url;
  }
  let newUrl = url;
  // filter the keys that are not in the url
  const keys = Object.keys(object).filter((key) => !newUrl.includes(`:${key}`));
  // replace the keys in the url
  Object.keys(object).forEach((key) => {
    newUrl = newUrl.replace(`:${key}`, object[key]);
  });
  // add the unmatched keys as query params
  const query = keys.map((key) => `${key}=${object[key]}`).join('&');
  if (query.length > 0) {
    newUrl += `?${query}`;
  }
  return newUrl;
};

/**
 * Checks if a string is valid (not null, undefined or empty)
 * @param {string} value The string to check
 */
export const isStringValid = (value: string) => {
  return value && value.trim().length > 0;
};

/**
 * Converts a string to a boolean
 * @example
 * convertToBoolean('true') // => true
 * convertToBoolean('false') // => false
 * convertToBoolean('') // => false
 * convertToBoolean(' ') // => false
 * convertToBoolean('true ') // => true
 * convertToBoolean('false ') // => false
 * @param {string} value The string to convert
 */
export const convertToBoolean = (value?: any) => {
  if (typeof value === 'boolean') return value;
  return value == null || value?.trim()?.length <= 0 ? false : value?.toLowerCase() === 'true';
};

/**
 * Validates that an email has the correct domain
 * @param {string} email - The email to validate
 * @param {string} domain - The domain that the email should have
 */
export const validateEmailDomain = (email: string, domain: string) => {
  // Extract the domain part from the email
  const emailDomain = email.substring(email.lastIndexOf('@') + 1);
  // Compare the extracted domain with the provided domain
  return emailDomain === domain;
};

/**
 * Clean string to only accept letters, numbers
 * @param {string} value - The string to clean
 * @returns {string} The cleaned string
 */
export const cleanString = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9]/g, '');
};
